import React, { useState, useEffect, useCallback } from 'react';
import { API } from '../App';
import AttachmentsWidget from '../components/AttachmentsWidget';

const COVERAGE_TYPES = [
  { key: 'marine_cargo', label: 'Marine Cargo' },
  { key: 'marine_hull',  label: 'Marine Hull' },
  { key: 'credit',       label: 'Credit Insurance' },
  { key: 'liability',    label: 'Liability' },
  { key: 'other',        label: 'Sonstige' },
];

const STATUSES = [
  { key: 'pending',   label: 'Ausstehend', color: 'var(--text3)' },
  { key: 'active',    label: 'Aktiv',      color: '#22c55e' },
  { key: 'claimed',   label: 'Schadenfall',color: '#f59e0b' },
  { key: 'expired',   label: 'Abgelaufen', color: '#ef4444' },
  { key: 'cancelled', label: 'Storniert',  color: 'var(--text3)' },
];

const EMPTY = {
  deal_reference: '', insurer: '', policy_number: '', coverage_type: 'marine_cargo',
  coverage_amount: '', currency: 'USD', premium: '',
  issue_date: '', expiry_date: '',
  cargo_desc: '', voyage_route: '', status: 'pending', notes: '',
};

function statusMeta(k) { return STATUSES.find(s => s.key === k) || STATUSES[0]; }

function StatusBadge({ status }) {
  const m = statusMeta(status);
  return (
    <span style={{ display: 'inline-flex', padding: '2px 8px', borderRadius: 4,
      fontSize: 11, fontWeight: 700, background: m.color + '22', color: m.color }}>
      {m.label}
    </span>
  );
}

function InsuranceModal({ record, onClose, onSaved }) {
  const [form, setForm] = useState(record ? { ...record } : { ...EMPTY });
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleSave = async () => {
    if (!form.insurer && !form.policy_number) { setErr('Versicherer oder Police-Nr. erforderlich'); return; }
    setSaving(true); setErr('');
    try {
      if (record) await API.put(`/insurance/${record.id}`, form);
      else        await API.post('/insurance', form);
      onSaved(); onClose();
    } catch (e) { setErr(e.response?.data?.detail || 'Fehler'); }
    finally { setSaving(false); }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" style={{ maxWidth: 680 }} onClick={e => e.stopPropagation()}>
        <div className="modal-title">{record ? 'Police bearbeiten' : 'Neue Versicherungspolice'}</div>
        {err && <div className="error-msg">{err}</div>}

        <div className="grid-2">
          <div className="form-group">
            <label className="form-label">Versicherer</label>
            <input className="form-input" value={form.insurer} onChange={e => set('insurer', e.target.value)} placeholder="z.B. Lloyd's of London" />
          </div>
          <div className="form-group">
            <label className="form-label">Policen-Nummer</label>
            <input className="form-input" value={form.policy_number} onChange={e => set('policy_number', e.target.value)} />
          </div>
        </div>

        <div className="grid-3">
          <div className="form-group">
            <label className="form-label">Deckungsart</label>
            <select className="form-input" value={form.coverage_type} onChange={e => set('coverage_type', e.target.value)}>
              {COVERAGE_TYPES.map(t => <option key={t.key} value={t.key}>{t.label}</option>)}
            </select>
          </div>
          <div className="form-group">
            <label className="form-label">Deckungssumme</label>
            <input className="form-input" value={form.coverage_amount} onChange={e => set('coverage_amount', e.target.value)} placeholder="z.B. 5,000,000" />
          </div>
          <div className="form-group">
            <label className="form-label">Währung</label>
            <select className="form-input" value={form.currency} onChange={e => set('currency', e.target.value)}>
              {['USD','EUR','GBP','CHF'].map(c => <option key={c}>{c}</option>)}
            </select>
          </div>
        </div>

        <div className="grid-3">
          <div className="form-group">
            <label className="form-label">Prämie</label>
            <input className="form-input" value={form.premium} onChange={e => set('premium', e.target.value)} placeholder="z.B. 12,500" />
          </div>
          <div className="form-group">
            <label className="form-label">Ausstellungsdatum</label>
            <input type="date" className="form-input" value={form.issue_date} onChange={e => set('issue_date', e.target.value)} />
          </div>
          <div className="form-group">
            <label className="form-label">Ablaufdatum</label>
            <input type="date" className="form-input" value={form.expiry_date} onChange={e => set('expiry_date', e.target.value)} />
          </div>
        </div>

        <div className="grid-2">
          <div className="form-group">
            <label className="form-label">Deal-Referenz</label>
            <input className="form-input" value={form.deal_reference} onChange={e => set('deal_reference', e.target.value)} />
          </div>
          <div className="form-group">
            <label className="form-label">Status</label>
            <select className="form-input" value={form.status} onChange={e => set('status', e.target.value)}>
              {STATUSES.map(s => <option key={s.key} value={s.key}>{s.label}</option>)}
            </select>
          </div>
        </div>

        <div className="form-group">
          <label className="form-label">Cargo-Beschreibung</label>
          <input className="form-input" value={form.cargo_desc} onChange={e => set('cargo_desc', e.target.value)} placeholder="z.B. 50,000 MT Crude Oil" />
        </div>
        <div className="form-group">
          <label className="form-label">Voyage Route</label>
          <input className="form-input" value={form.voyage_route} onChange={e => set('voyage_route', e.target.value)} placeholder="z.B. Ras Tanura → Rotterdam" />
        </div>
        <div className="form-group">
          <label className="form-label">Notizen</label>
          <textarea className="form-input" rows={2} value={form.notes} onChange={e => set('notes', e.target.value)} />
        </div>

        <div className="modal-footer">
          <button className="btn btn-ghost" onClick={onClose}>Abbrechen</button>
          <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
            {saving ? 'Speichern…' : 'Speichern'}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function InsurancePage() {
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ]             = useState('');
  const [statusFilter, setSt] = useState('');
  const [modal, setModal]     = useState(null);
  const [detail, setDetail]   = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (statusFilter) params.status = statusFilter;
      if (q) params.q = q;
      const r = await API.get('/insurance', { params });
      setRecords(r.data);
    } catch { /* ignore */ }
    finally { setLoading(false); }
  }, [q, statusFilter]);

  useEffect(() => { load(); }, [load]);

  if (detail) {
    return (
      <div className="page-content">
        {modal === 'edit' && (
          <InsuranceModal record={detail} onClose={() => setModal(null)}
            onSaved={async () => { const r = await API.get(`/insurance/${detail.id}`); setDetail(r.data); load(); }} />
        )}
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20 }}>
          <button className="btn btn-ghost btn-sm" onClick={() => setDetail(null)}>← Zurück</button>
          <h1 style={{ fontSize: 18, fontWeight: 700 }}>🛡 {detail.insurer || 'Police'} — {detail.policy_number || detail.coverage_type}</h1>
          <StatusBadge status={detail.status} />
          <div style={{ marginLeft: 'auto' }}>
            <button className="btn btn-ghost btn-sm" onClick={() => setModal('edit')}>Bearbeiten</button>
          </div>
        </div>
        <div className="grid-2">
          <div className="card">
            <div className="card-title">Police Details</div>
            {[
              ['Versicherer', detail.insurer], ['Policen-Nr.', detail.policy_number],
              ['Deckungsart', COVERAGE_TYPES.find(t => t.key === detail.coverage_type)?.label || detail.coverage_type],
              ['Deckungssumme', detail.coverage_amount ? `${detail.coverage_amount} ${detail.currency}` : null],
              ['Prämie', detail.premium ? `${detail.premium} ${detail.currency}` : null],
              ['Ausgestellt', detail.issue_date], ['Ablauf', detail.expiry_date],
              ['Deal', detail.deal_reference],
            ].filter(([,v]) => v).map(([k,v]) => (
              <div key={k} style={{ display:'flex', gap:8, padding:'6px 0', borderBottom:'1px solid var(--border)', fontSize:13 }}>
                <span style={{ color:'var(--text3)', minWidth:130 }}>{k}</span>
                <span style={{ color: k === 'Ablauf' && detail.expiring_soon ? 'var(--warn)' : 'var(--text1)' }}>
                  {v}{k === 'Ablauf' && detail.expiring_soon ? ' ⚠ läuft bald ab' : ''}
                </span>
              </div>
            ))}
          </div>
          <div className="card">
            <div className="card-title">Cargo & Voyage</div>
            {[['Cargo', detail.cargo_desc], ['Route', detail.voyage_route]].filter(([,v]) => v).map(([k,v]) => (
              <div key={k} style={{ display:'flex', gap:8, padding:'6px 0', borderBottom:'1px solid var(--border)', fontSize:13 }}>
                <span style={{ color:'var(--text3)', minWidth:80 }}>{k}</span>
                <span style={{ color:'var(--text1)' }}>{v}</span>
              </div>
            ))}
            {detail.notes && <p style={{ fontSize:13, color:'var(--text2)', marginTop:8, whiteSpace:'pre-wrap' }}>{detail.notes}</p>}
          </div>
        </div>
        <div className="card" style={{ marginTop: 16 }}>
          <div className="card-title">📎 Anhänge</div>
          <AttachmentsWidget entityType="insurance" entityId={detail.id} />
        </div>
      </div>
    );
  }

  return (
    <div className="page-content">
      {modal === 'create' && (
        <InsuranceModal onClose={() => setModal(null)} onSaved={() => { setModal(null); load(); }} />
      )}

      <div className="page-header" style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start' }}>
        <div>
          <h1>🛡 Versicherungen</h1>
          <p>Marine Cargo, Hull, Credit Insurance — Policen & Ablaufüberwachung</p>
        </div>
        <button className="btn btn-primary" onClick={() => setModal('create')}>+ Neue Police</button>
      </div>

      {!loading && (
        <div style={{ display:'flex', gap:10, flexWrap:'wrap', marginBottom:20 }}>
          {[
            { label:'Gesamt', value:records.length, color:'var(--accent)' },
            { label:'Aktiv', value:records.filter(r=>r.status==='active').length, color:'#22c55e' },
            { label:'Läuft ab (30d)', value:records.filter(r=>r.expiring_soon).length, color:'var(--warn)' },
          ].map(c => (
            <div key={c.label} className="card" style={{ minWidth:110, textAlign:'center', padding:'12px 16px' }}>
              <div style={{ fontSize:26, fontWeight:800, color:c.color }}>{c.value}</div>
              <div style={{ fontSize:11, color:'var(--text3)' }}>{c.label}</div>
            </div>
          ))}
        </div>
      )}

      <div style={{ display:'flex', gap:10, marginBottom:16, flexWrap:'wrap' }}>
        <input className="form-input" style={{ maxWidth:240 }} placeholder="Suche Versicherer, Deal, Policen-Nr.…"
          value={q} onChange={e => setQ(e.target.value)} />
        <select className="form-input" style={{ maxWidth:180 }} value={statusFilter} onChange={e => setSt(e.target.value)}>
          <option value="">Alle Status</option>
          {STATUSES.map(s => <option key={s.key} value={s.key}>{s.label}</option>)}
        </select>
        <button className="btn btn-ghost btn-sm" onClick={load}>⟳</button>
      </div>

      {loading && <div className="loading">Lade…</div>}

      {!loading && records.length === 0 && (
        <div style={{ textAlign:'center', padding:60, color:'var(--text3)' }}>
          <div style={{ fontSize:40, marginBottom:12 }}>🛡</div>
          <div style={{ fontSize:15, fontWeight:600 }}>Keine Policen erfasst</div>
        </div>
      )}

      <table className="tbl">
        {records.length > 0 && (
          <thead><tr>
            <th>Versicherer / Police</th><th>Deckungsart</th><th>Summe</th>
            <th>Deal</th><th>Ablauf</th><th>Status</th><th></th>
          </tr></thead>
        )}
        <tbody>
          {records.map(r => (
            <tr key={r.id} style={{ cursor:'pointer' }} onClick={() => setDetail(r)}>
              <td>
                <div style={{ fontWeight:600 }}>{r.insurer || '—'}</div>
                {r.policy_number && <div style={{ fontSize:11, color:'var(--text3)' }}>{r.policy_number}</div>}
              </td>
              <td style={{ color:'var(--text2)' }}>{COVERAGE_TYPES.find(t=>t.key===r.coverage_type)?.label || r.coverage_type}</td>
              <td>{r.coverage_amount ? `${r.coverage_amount} ${r.currency}` : '—'}</td>
              <td style={{ color:'var(--text2)' }}>{r.deal_reference || '—'}</td>
              <td style={{ color: r.expiring_soon ? 'var(--warn)' : 'var(--text2)' }}>
                {r.expiry_date || '—'}{r.expiring_soon ? ' ⚠' : ''}
              </td>
              <td><StatusBadge status={r.status} /></td>
              <td onClick={e => e.stopPropagation()}>
                <div style={{ display:'flex', gap:6 }}>
                  <button className="btn btn-ghost btn-sm" onClick={() => setModal(r)}>✏</button>
                  <button className="btn btn-danger btn-sm" onClick={async () => {
                    if (!window.confirm('Löschen?')) return;
                    await API.delete(`/insurance/${r.id}`); load();
                  }}>🗑</button>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
